import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
} from "@apollo/client";
import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev";

import { isDevEnvs } from "helpers/env-checks";

const KEY = import.meta.env.PLATFORM_AUTH_KEY as string;

const setupPaginationMerge = <X extends { edges?: Array<unknown> | null }>(
  keyArgs: Array<string>,
) => ({
  keyArgs,
  merge(existing: X | undefined, incoming: X) {
    if (!existing) {
      return incoming;
    }

    return {
      ...incoming,
      edges: [...(existing.edges ?? []), ...(incoming.edges ?? [])],
    };
  },
});

const setupReplaceArrayField = (keyArgs: Array<string>, field: string) => ({
  [field]: {
    keyArgs,
    merge: (_existing: Array<unknown> | undefined, incoming: Array<unknown>) =>
      incoming ?? [],
  },
});

// load error messages during development
if (import.meta.env.DEV) {
  loadDevMessages();
  loadErrorMessages();
}

ApolloLink.empty();

// init without token not to connect to the backend
export const makeApolloClient = (token?: string) =>
  new ApolloClient({
    devtools: {
      enabled: isDevEnvs(import.meta.env.LH_ENV_NAME as string),
      name: "Storage Apollo Client",
    },
    cache: new InMemoryCache({
      typePolicies: {
        // remove this when backend renames this object per query
        ListItemOfString: {
          keyFields: false,
        },
        ListItemWithDataOfString: {
          keyFields: false,
        },
        RiskManagementMajorEventComparisonChart: {
          merge: true,
        },
        RiskManagementOverallComparisonChart: {
          merge: true,
        },
        Query: {
          fields: {
            riskManagementChartsForWellNode: {
              keyArgs: ["hierarchyId", "wellName"],
              merge: true,
            },
            riskManagementMajorEventComparisonChart: {
              keyArgs: ["hierarchyId", "wellName"],
            },
            riskManagementOverallComparisonChart: {
              keyArgs: ["hierarchyId", "wellName"],
            },
            systemOverviewRiskSummary: {
              merge: true,
            },
            systemOverviewAssetsTable: setupPaginationMerge([
              "hierarchyIds",
              "order",
            ]),
            systemOverviewRiskTable: setupPaginationMerge([
              "hierarchyIds",
              "order",
            ]),
            riskManagementTable: setupPaginationMerge([
              "failureModeId",
              "failureEffectId",
              "hierarchyIds",
              "order",
            ]),
            ...setupReplaceArrayField(["hierarchyIds"], "map"),
          },
        },
      },
    }),
    link: token
      ? new HttpLink({
          uri: `${import.meta.env.API_BASE_URL}/graphql`,
          headers: {
            Authorization: `Bearer ${token}`,
            "Ocp-Apim-Subscription-Key": KEY,
          },
        })
      : ApolloLink.empty(),
  });

export default makeApolloClient;
